import { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { frontendDb as db } from '../lib/frontend-db';

const VereineSection = () => {
  const [vereine, setVereine] = useState([
    {
      id: 'bieber',
      title: 'Liederkranz Bieber',
      description: 'Erfahren Sie mehr über den Liederkranz Bieber und unsere lange Tradition seit 1847.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=300&fit=crop',
      url: 'https://www.liederkranz-bieber.de'
    },
    {
      id: 'wiesen',
      title: 'Liederkranz Wiesen',
      description: 'Entdecken Sie den Liederkranz Wiesen und erfahren Sie mehr über diesen Teil unseres Chor-Zusammenschlusses.',
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=500&h=300&fit=crop',
      url: 'https://www.liederkranz-wiesen.de'
    }
  ]);
  const [loading, setLoading] = useState(true);
  const [sectionTitle, setSectionTitle] = useState('Unsere Vereine');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settings = await db.getSettings();
      if (settings) {
        setSectionTitle((settings as any).vereine_title || 'Unsere Vereine');
        setVereine([
          {
            id: 'bieber',
            title: (settings as any).bieber_title || 'Liederkranz Bieber',
            description: (settings as any).bieber_description || 'Erfahren Sie mehr über den Liederkranz Bieber und unsere lange Tradition seit 1847.',
            image: (settings as any).bieber_info_hero_image || (settings as any).bieber_image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=300&fit=crop',
            url: (settings as any).bieber_url || 'https://www.liederkranz-bieber.de'
          },
          {
            id: 'wiesen',
            title: (settings as any).wiesen_title || 'Liederkranz Wiesen',
            description: (settings as any).wiesen_description || 'Entdecken Sie den Liederkranz Wiesen und erfahren Sie mehr über diesen Teil unseres Chor-Zusammenschlusses.',
            image: (settings as any).wiesen_info_hero_image || (settings as any).wiesen_image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=500&h=300&fit=crop',
            url: (settings as any).wiesen_url || 'https://www.liederkranz-wiesen.de'
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading vereine settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (url: string, id: string) => {
    if (id === 'wiesen') {
      // For Wiesen, navigate to our info page
      window.location.href = '/wiesen-info';
    } else if (id === 'bieber') {
      // For Bieber, navigate to our info page
      window.location.href = '/bieber-info';
    } else {
      // For external links, open in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <section id="vereine" className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-8 text-center">
            {sectionTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-6 space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-3/4" />
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="vereine" className="py-16 dark:bg-slate-800 transition-colors">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-8 text-center">
          Unsere Vereine
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {vereine.map((verein) => (
            <div
              key={verein.id}
              onClick={() => handleClick(verein.url, verein.id)}
              className="rounded-lg shadow-md overflow-hidden transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer group"
              style={{ backgroundColor: 'var(--bg-primary)' }}
            >
              <div className="relative h-48">
                <img
                  src={verein.image}
                  alt={verein.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ExternalLink className="text-white" size={24} />
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-semibold text-[var(--primary-color)]">
                    {verein.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-200 mb-4">
                  {verein.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-[var(--primary-color)] font-semibold group-hover:text-[var(--primary-color)]/80 transition-colors">
                    <span>Besuchen</span>
                  </div>
                  {(verein.id === 'wiesen' || verein.id === 'bieber') && (
                    <a href={`/${verein.id}-info`} className="text-sm hover:text-[var(--primary-color)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
                      Mehr Info →
                    </a>
                  )}
                  {verein.id !== 'wiesen' && verein.id !== 'bieber' && (
                    <ExternalLink className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity" size={16} />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VereineSection;

